import './Selects.css'

import { memo, useEffect, useRef, useState } from 'react'

import { AngleIcon } from '../../../components/Icons'
import { useClickOutside } from '../../../hooks/useClickOutside'

interface ISelectWithSearchOptProps {
  options: string[]
  selectedOption: string
  setDefaultOption: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  onInputChange?: (value: string) => void
  onSelect: (option: string) => void
  isFetching?: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  fetchNextPage?: () => void
}

const SelectWithSearchOpt: React.FC<ISelectWithSearchOptProps> = memo(
  ({
    options,
    selectedOption,
    setDefaultOption,
    onSelect,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    onInputChange,
    fetchNextPage,
  }) => {
    const containerRef = useRef(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)

    const [showOptions, setShowOptions] = useState(false)
    const [selected, setSelected] = useState<string>(selectedOption)
    const [inputValue, setInputValue] = useState('')

    useClickOutside(containerRef, () => {
      setShowOptions(false)
      setInputValue('')
    })

    const toggleOptions = () => {
      setShowOptions(!showOptions)
      if (!showOptions) {
        setInputValue('')
        if (onInputChange) onInputChange('')
      }
      setTimeout(() => {
        if (!showOptions) {
          inputRef.current?.focus()
        }
      })
    }

    useEffect(() => {
      const el = scrollRef.current
      if (!el) return
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = el
        const nearBottom = scrollTop + clientHeight >= scrollHeight - 50
        if (nearBottom && hasNextPage && !isFetchingNextPage && fetchNextPage) {
          fetchNextPage()
        }
      }
      el.addEventListener('scroll', handleScroll)
      return () => el.removeEventListener('scroll', handleScroll)
    }, [showOptions, isFetchingNextPage, hasNextPage, fetchNextPage])

    const clearFilter = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      e.stopPropagation()
      setSelected(options[0])
      setDefaultOption(e)
    }

    const handleClick = (option: string) => {
      setSelected(option)
      onSelect(option)
      setShowOptions(false)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      if (onInputChange) onInputChange(e.target.value)
    }

    return (
      <div ref={containerRef}>
        <div
          onClick={toggleOptions}
          className="cursor-pointer hover:bg-opacity-20 tc-mv-select-selectbox"
        >
          <div className="tc-mv-select-selected-option truncate">
            {selected}
          </div>
          <div className="flex gap-2 items-center">
            {selected === options[0] ? null : (
              <button
                onClick={clearFilter}
                className="tc-mv-select-selectbox-arrow"
              >
                <i className="fa-solid fa-xmark text-xl" />
              </button>
            )}
            <div className="tc-mv-select-selectbox-arrow">
              <AngleIcon />
            </div>
          </div>
        </div>
        {showOptions && (
          <div className="px-4 py-4 tc-mv-select-options">
            <div ref={scrollRef} className="h-full w-full overflow-auto mt-12">
              <div className="absolute top-0 left-0 right-0 w-full px-4 pt-4 z-10">
                <div className="tc-mv-option-search-wrapper">
                  <input
                    ref={inputRef}
                    className=" !mb-0 w-full"
                    placeholder="Search"
                    value={inputValue}
                    onChange={handleInputChange}
                  />
                  {isFetching && !isFetchingNextPage && inputValue.trim() && (
                    <div>
                      <svg
                        className="animate-spin h-5 w-5 text-gray-400"
                        viewBox="3 3 18 18"
                      >
                        <path
                          fill="currentColor"
                          d="M12 3v2a7 7 0 1 0 7 7h2a9 9 0 1 1-9-9z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
              {options.map(option => (
                <div
                  key={option}
                  className={`rounded-xl text-sm w-full break-words cursor-pointer hover:bg-black hover:bg-opacity-20 px-4 py-[10px] ${
                    selected === option ? 'bg-black bg-opacity-20' : ''
                  }`}
                  onClick={() => {
                    handleClick(option)
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  },
)

export { SelectWithSearchOpt }
